import { useRef, useEffect } from 'react';
import { useAuth } from '../../stores/auth';
import { useTeamOverview } from '../../hooks/use-team-overview';
import { useOffline } from '../../hooks/use-offline';
import { useToast } from '../../components/Toast';
import { PullToRefresh } from '../../components/PullToRefresh';
import PromoterCard from '../../components/supervisor/PromoterCard';
import { Users, Clock, AlertTriangle, WifiOff } from 'lucide-react';

export default function SupervisorHome() {
  const { user } = useAuth();
  const { data, isLoading, refetch, cachedTimestamp } = useTeamOverview();
  const { isOnline } = useOffline();
  const { toast } = useToast();
  const prevAlertCount = useRef(0);

  // Alert toast + vibration when new alerts appear
  useEffect(() => {
    if (!data) return;
    const currentCount = data.alerts.filter(a => a.status === 'OPEN').length;
    if (currentCount > prevAlertCount.current && prevAlertCount.current > 0) {
      const newest = data.alerts[0];
      toast({ title: newest?.message || 'Novo alerta', variant: newest?.severity === 'CRITICAL' ? 'error' : 'default' });
      if (newest?.severity === 'CRITICAL') navigator.vibrate?.(200);
    }
    prevAlertCount.current = currentCount;
  }, [data, toast]);

  const greeting = new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite';

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 space-y-4">
        <div className="h-16 bg-gray-200 rounded-xl animate-pulse" />
        <div className="flex gap-3">
          {[1, 2, 3].map(i => <div key={i} className="flex-1 h-20 bg-gray-200 rounded-xl animate-pulse" />)}
        </div>
        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  const { summary, promoters, alerts } = data;
  const openAlerts = alerts.filter(a => a.status === 'OPEN');

  return (
    <PullToRefresh onRefresh={() => refetch().then(() => {})}>
    <div className="min-h-screen bg-gray-50 pb-20">
      {!isOnline && cachedTimestamp && (
        <div className="bg-amber-500 text-white text-xs text-center py-1.5 flex items-center justify-center gap-1">
          <WifiOff size={12} />
          Sem conexão — dados de {new Date(cachedTimestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      <div className="bg-white px-4 pt-safe pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{greeting}, {user?.name?.split(' ')[0]}</h1>
            <p className="text-xs text-gray-500">{summary.total_promoters} promotores · {summary.total_stores_today} lojas hoje</p>
          </div>
          <span className="text-xs text-gray-400">{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 border border-green-100 rounded-xl p-3 text-center">
            <Users size={18} className="mx-auto text-green-600 mb-1" />
            <p className="text-xl font-bold text-green-700">{summary.in_field}</p>
            <p className="text-[10px] text-green-600 font-medium">Em campo</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
            <Clock size={18} className="mx-auto text-amber-600 mb-1" />
            <p className="text-xl font-bold text-amber-700">{summary.delayed}</p>
            <p className="text-[10px] text-amber-600 font-medium">Atrasado</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
            <AlertTriangle size={18} className="mx-auto text-red-600 mb-1" />
            <p className="text-xl font-bold text-red-700">{summary.absent}</p>
            <p className="text-[10px] text-red-600 font-medium">Ausente</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-600">Progresso do dia</span>
            <span className="text-xs font-semibold text-primary">{summary.completion_rate}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${summary.completion_rate}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{summary.stores_visited} de {summary.total_stores_today} lojas visitadas</p>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Equipe hoje</h2>
          {promoters.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <Users size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Nenhum promotor vinculado</p>
              <p className="text-xs text-gray-400">Contate o administrador</p>
            </div>
          ) : (
            <div className="space-y-2">
              {promoters.map(p => <PromoterCard key={p.promoter_id} promoter={p} />)}
            </div>
          )}
        </div>

        {openAlerts.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Alertas ({openAlerts.length})</h2>
            <div className="space-y-2">
              {openAlerts.slice(0, 3).map(alert => (
                <div key={alert.id} className={`rounded-xl border p-3 ${
                  alert.severity === 'CRITICAL' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className="text-xs font-medium text-gray-900">{alert.message}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{alert.promoter_name} · {new Date(alert.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </PullToRefresh>
  );
}
